import { useMemo, useCallback, useState } from 'react';
import { Col, Row, Upload } from 'antd';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  UploadOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Button } from 'components';
import { useTranslation } from 'react-i18next';
import Helpers from 'helpers';

import QRsApi from 'libs/api/promo/qrs';
import { useApi } from 'hooks/useApi';
import { useFilter } from '../../../../hooks/useFilter';

const api = new QRsApi();

const useQR = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { filterData } = useFilter();
  const [currentFileList, setCurrentFileList] = useState([]);
  const [currentFile, setCurrentFile] = useState({});
  const {
    data,
    loading,
    onChangeTableFilter,
    updateInProcess,
    setUpdateInProcess,
    fetchList,
  } = useApi({
    resourceKey: 'qrs',
    singleResourceKey: 'qr',
    api,
    isMainPage: true,
  });

  const handleUploadFile = useCallback(async () => {
    setUpdateInProcess(true);
    try {
      const formData = new FormData();
      formData.append('company_id', currentFile.id);
      formData.append('file', currentFile.file);
      setCurrentFileList([]);
      setCurrentFile({});
      await api.uploadFile(formData);
      await fetchList();
    } catch (err) {
      console.log(err);
    } finally {
      setUpdateInProcess(false);
    }
  }, [setUpdateInProcess, currentFile]);

  const filters = useMemo(
    () => [
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_title',
        values: [],
        isActiveSearch: true,
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company_id',
        isSearch: true,
        values: filterData?.companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
        inactiveValues: filterData?.inactive_companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
      },
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'shopping_center',
        valueType: 'number',
        values: filterData?.shopping_centers?.map((shopping_center) => ({
          title: shopping_center.title,
          value: +shopping_center.value,
        })),
      },
    ],
    [t, filterData],
  );

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        fixed: 'left',
        sorter: () => {},
        render: (data, row) => row?.shopping_center,
      },

      {
        title: t('Insegna'),
        dataIndex: 'companies.title',
        sorter: () => {},
        render: (data, row) => row?.title,
      },
      {
        title: t('Visualizzazioni'),
        dataIndex: 'count_open',
        sorter: () => {},
      },
      {
        title: t('QR'),
        dataIndex: 'file_url',
        render: (data, row) => (
          <a
            onClick={(e) => {
              e.preventDefault();
              Helpers.axiosDownload(data, row.file_title);
            }}
            href={data}
          >
            {t('Scarica')}
          </a>
        ),
      },
      {
        title: t('Short link'),
        dataIndex: 'short_url',
        sorter: () => {},
      },
      {
        title: t('Scarica file'),
        dataIndex: 'pdf_url',
        render: (data) =>
          data && (
            <Button
              htmlFor='download-file'
              type='primary-white'
              icon={<DownloadOutlined style={{ marginRight: '10px' }} />}
            >
              <a
                id='download-file'
                target='_blank'
                rel='noopener noreferrer'
                href={data}
              >
                {t('Scarica File')}
              </a>
            </Button>
          ),
      },
      {
        title: t('Carica file'),
        dataIndex: 'company_id',
        render: (company_id) => {
          return (
            <Upload
              name='logo'
              listType='picture'
              fileList={currentFile.id === company_id ? currentFileList : []}
              onChange={(file, ...rest) => {
                setCurrentFileList(file.fileList);
                if (file.fileList.length) {
                  setCurrentFile({
                    id: company_id,
                    file: file.file,
                  });
                } else {
                  setCurrentFile({});
                }
              }}
              multiple={false}
              beforeUpload={() => false}
              onRemove={() => {
                setCurrentFileList([]);
              }}
            >
              <Button type={'primary-white'} icon={<UploadOutlined />}>
                {t('Carica file')}
              </Button>
            </Upload>
          );
        },
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 200,
        fixed: 'right',
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'center'}
            onClick={(e) => e.stopPropagation()}
          >
            <Col>
              <Button
                onClick={handleUploadFile}
                disabled={currentFile.id !== row.company_id}
                loading={updateInProcess}
              >
                {t('Submit')}
              </Button>
            </Col>
            <Col>
              <Link to={`${pathname}/${row.company_id}`}>
                <Button type={'primary-white'} icon={<InfoCircleOutlined />} />
              </Link>
            </Col>
          </Row>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, handleUploadFile, updateInProcess]);

  return {
    columns,
    data,
    loading,
    filters,
    onChangeTableFilter,
  };
};

export default useQR;
